/**
 * Store Pinia — orderWorkflows
 *
 * Migré depuis `store/modules/orderWorkflows.js`.
 * Cache des configurations de workflow chargées depuis l'API.
 *
 * Équivalences Vuex → Pinia :
 *   - getters getConfigurations / isLoaded / findConfig → getters (findConfig reste paramétrique)
 *   - mutations SET_CONFIGURATIONS / ADD / UPDATE / REMOVE / INVALIDATE_CACHE → actions
 *   - actions async fetch / save / delete → actions (mutent directement le state)
 *
 * Non persisté → persisté : voir `persist: true` en bas (comme l'ancien module Vuex).
 */
import { defineStore } from 'pinia'
import { orderWorkflowsService } from '@/services'
import logger from '@/utils/logger'

export const useOrderWorkflowsStore = defineStore('orderWorkflows', {
  state: () => ({
    configurations: [],
    loaded: false,
  }),

  getters: {
    getConfigurations: (state) => state.configurations,
    isLoaded: (state) => state.loaded,

    /**
     * Trouve une configuration par type + fournisseur + critères. Getter paramétrique :
     * renvoie une fonction (même signature qu'en Vuex).
     */
    findConfig: (state) => ({ type, fournisseur, migration_type_list = null, migration_type = null, livraison = null, typeequipement = null, modepaiement = null }) => { // eslint-disable-line camelcase
      if (!state.configurations || !Array.isArray(state.configurations)) return null
      return state.configurations.find(c =>
        c.type === type &&
        c.fournisseur === fournisseur &&
        c.livraison === livraison &&
        c.mode_paiement === modepaiement && // eslint-disable-line camelcase
        c.type_equipement === typeequipement && // eslint-disable-line camelcase
        c.migration_type === migration_type && // eslint-disable-line camelcase
        JSON.stringify(c.migration_type_list) === JSON.stringify(migration_type_list), // eslint-disable-line camelcase
      ) || null
    },
  },

  actions: {
    // ─── Mutations d'état (ex-mutations Vuex) ─────────────────────────────
    setConfigurations (configurations) {
      this.configurations = configurations
      this.loaded = true
    },
    invalidateCache () {
      this.configurations = []
      this.loaded = false
    },
    addConfiguration (config) {
      if (!Array.isArray(this.configurations)) this.configurations = []
      this.configurations.push(config)
    },
    updateConfiguration (updated) {
      if (!Array.isArray(this.configurations)) this.configurations = []
      const index = this.configurations.findIndex(c => c.id === updated.id)
      if (index !== -1) {
        this.configurations.splice(index, 1, updated)
      }
    },
    removeConfiguration (id) {
      if (!Array.isArray(this.configurations)) this.configurations = []
      this.configurations = this.configurations.filter(c => c.id !== id)
    },

    // ─── Actions async (API) ──────────────────────────────────────────────
    async fetchConfigurations () {
      try {
        const data = await orderWorkflowsService.list()
        this.setConfigurations(data)
        return data
      } catch (error) {
        logger.error('Erreur chargement configurations workflow:', error)
        throw error
      }
    },

    async saveConfiguration (configuration) {
      try {
        if (configuration.id) {
          const data = await orderWorkflowsService.update(configuration, configuration.id)
          this.updateConfiguration(data)
          return data
        }
        const data = await orderWorkflowsService.save(configuration)
        this.addConfiguration(data)
        return data
      } catch (error) {
        logger.error('Erreur sauvegarde configuration workflow:', error)
        throw error
      }
    },

    async deleteConfiguration (id) {
      try {
        await orderWorkflowsService.remove(id)
        this.removeConfiguration(id)
      } catch (error) {
        logger.error('Erreur suppression configuration workflow:', error)
        throw error
      }
    },
  },

  // Persisté (comme l'ancien module Vuex, qui n'était pas dans la liste d'exclusion
  // du persistedState). Évite de recharger les configs à chaque rafraîchissement.
  persist: true,
})
